import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardApi } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { House, Briefcase, Phone, CalendarX, Clock, Warning, Users, UserCirclePlus, ChartLine, CaretRight } from '@phosphor-icons/react';

const TYPE_LABELS = {
  office: 'Presente', smart_working: 'Smart Working', on_call: 'Reperibilità',
  leave: 'Ferie/Assenza', permission: 'Permesso', overtime: 'Straordinario',
};
const TYPE_COLORS = {
  office: '#22C55E', smart_working: '#002FA7', on_call: '#FF2A00',
  leave: '#F59E0B', permission: '#8B5CF6', overtime: '#059669',
};
const TYPE_ICONS = {
  office: House, smart_working: Briefcase, on_call: Phone,
  leave: CalendarX, permission: Clock, overtime: Clock,
};

const StatCard = ({ icon: Icon, label, value, color, onClick }) => (
  <button onClick={onClick}
    className="bg-white border border-[#E4E4E7] p-5 text-left hover:border-[#0A0A0A] transition-colors group w-full"
    data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon size={20} weight="bold" style={{ color }} />
      </div>
      {onClick && <CaretRight size={16} className="text-[#A1A1AA] group-hover:text-[#0A0A0A] transition-colors" />}
    </div>
    <p className="text-2xl font-bold font-heading tracking-tight" style={{ color }}>{value}</p>
    <p className="text-xs text-[#52525B] font-medium uppercase tracking-wider mt-1">{label}</p>
  </button>
);

const Dashboard = () => {
  const { user, isManager, isTeamLeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await dashboardApi.getStats();
        setStats(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const isAdmin = user?.role === 'super_admin';
  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Buongiorno';
    if (h < 18) return 'Buon pomeriggio';
    return 'Buonasera';
  };

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0A0A0A]">
          {greeting()}, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-[#52525B] mt-1">
          {isAdmin ? 'Panoramica amministratore di sistema' :
           isManager ? 'Panoramica gestione team' :
           isTeamLeader ? 'Panoramica team leader' :
           'Il tuo riepilogo personale'}
        </p>
      </div>

      {/* My Today Status (all roles) */}
      {stats?.my_today_types?.length > 0 && (
        <div className="mb-6 bg-white border border-[#E4E4E7] p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-[#52525B] mb-3">Il tuo stato oggi</p>
          <div className="flex items-center gap-3 flex-wrap">
            {stats.my_today_types.map((t, i) => {
              const Icon = TYPE_ICONS[t] || Clock;
              return (
                <div key={i} className="flex items-center gap-2 px-3 py-1.5 border" style={{ borderColor: TYPE_COLORS[t], backgroundColor: `${TYPE_COLORS[t]}10` }}>
                  <Icon size={16} weight="bold" style={{ color: TYPE_COLORS[t] }} />
                  <span className="text-sm font-bold" style={{ color: TYPE_COLORS[t] }}>{TYPE_LABELS[t]}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
        {/* Today counts */}
        <StatCard icon={House} label="Presenti Oggi" value={stats?.today_office || 0} color="#22C55E" onClick={() => navigate('/calendar')} />
        <StatCard icon={Briefcase} label="Smart Working" value={stats?.today_smart_working || 0} color="#002FA7" onClick={() => navigate('/calendar')} />
        <StatCard icon={Phone} label="Reperibili" value={stats?.today_on_call || 0} color="#FF2A00" onClick={() => navigate('/calendar')} />
        <StatCard icon={CalendarX} label="In Ferie/Assenza" value={stats?.today_leave || 0} color="#F59E0B" onClick={() => navigate('/calendar')} />
      </div>

      {/* Role-specific sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Left column: personal info */}
        <div className="space-y-6">
          {/* Balances */}
          <div className="bg-white border border-[#E4E4E7]">
            <div className="px-5 py-3 border-b border-[#E4E4E7]">
              <p className="text-xs font-bold uppercase tracking-wider text-[#52525B]">I tuoi saldi</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-[#52525B]">Ferie</span>
                  <span className="font-bold font-heading text-lg">{stats?.leave_balance || 0}h</span>
                </div>
                <div className="h-1.5 bg-[#E4E4E7]">
                  <div className="h-full bg-[#F59E0B]" style={{ width: `${Math.min(100, (stats?.leave_balance || 0) / 1.6)}%` }}></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-[#52525B]">ROL</span>
                  <span className="font-bold font-heading text-lg">{stats?.rol_balance || 0}h</span>
                </div>
                <div className="h-1.5 bg-[#E4E4E7]">
                  <div className="h-full bg-[#8B5CF6]" style={{ width: `${Math.min(100, (stats?.rol_balance || 0) / 0.8)}%` }}></div>
                </div>
              </div>
            </div>
          </div>

          {/* My pending requests */}
          <div className="bg-white border border-[#E4E4E7] p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wider text-[#52525B]">Le tue richieste in attesa</p>
              <span className="text-2xl font-bold font-heading">{stats?.my_pending_requests || 0}</span>
            </div>
            {stats?.my_pending_requests > 0 && (
              <button onClick={() => navigate('/my-requests')} className="text-xs text-[#002FA7] font-bold mt-2 hover:underline" data-testid="go-my-requests">
                Vai alle richieste →
              </button>
            )}
          </div>
        </div>

        {/* Center column: upcoming schedules */}
        <div className="bg-white border border-[#E4E4E7]">
          <div className="px-5 py-3 border-b border-[#E4E4E7]">
            <p className="text-xs font-bold uppercase tracking-wider text-[#52525B]">Prossimi turni (7 giorni)</p>
          </div>
          <div className="divide-y divide-[#E4E4E7]">
            {stats?.upcoming_schedules?.length > 0 ? stats.upcoming_schedules.map((s, i) => {
              const Icon = TYPE_ICONS[s.type] || Clock;
              return (
                <div key={i} className="px-5 py-3 flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${TYPE_COLORS[s.type]}15` }}>
                    <Icon size={16} weight="bold" style={{ color: TYPE_COLORS[s.type] }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{TYPE_LABELS[s.type]}</p>
                    <p className="text-[10px] font-mono text-[#52525B]">{s.start}{s.start !== s.end ? ` → ${s.end}` : ''}</p>
                  </div>
                </div>
              );
            }) : (
              <div className="px-5 py-8 text-center text-sm text-[#A1A1AA]">Nessun turno nei prossimi 7 giorni</div>
            )}
          </div>
        </div>

        {/* Right column: role-specific */}
        <div className="space-y-6">
          {/* Pending approvals (TL/Manager/Admin) */}
          {(isAdmin || isManager || isTeamLeader) && (
            <div className="bg-white border border-[#E4E4E7] p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase tracking-wider text-[#52525B]">Approvazioni in attesa</p>
                <span className="text-2xl font-bold font-heading" style={{ color: stats?.pending_approvals > 0 ? '#FF2A00' : '#22C55E' }}>
                  {stats?.pending_approvals || 0}
                </span>
              </div>
              {stats?.pending_approvals > 0 && (
                <button onClick={() => navigate('/approvals')} className="text-xs text-[#002FA7] font-bold hover:underline" data-testid="go-approvals">
                  Gestisci approvazioni →
                </button>
              )}
            </div>
          )}

          {/* Admin-specific stats */}
          {(isAdmin || isManager) && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <StatCard icon={Users} label="Utenti Totali" value={stats?.total_users || 0} color="#0A0A0A" onClick={() => navigate('/users')} />
                <StatCard icon={ChartLine} label="Team" value={stats?.total_teams || 0} color="#002FA7" onClick={() => navigate('/organization')} />
              </div>
              {stats?.open_anomalies > 0 && (
                <div className="bg-[#FF2A00]/5 border border-[#FF2A00] p-5">
                  <div className="flex items-center gap-3">
                    <Warning size={20} weight="bold" className="text-[#FF2A00]" />
                    <div>
                      <p className="text-sm font-bold text-[#FF2A00]">{stats.open_anomalies} segnalazioni aperte</p>
                      <button onClick={() => navigate('/anomalies')} className="text-xs text-[#FF2A00] font-bold hover:underline" data-testid="go-anomalies">
                        Gestisci →
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {stats?.inactive_users > 0 && (
                <div className="bg-[#F59E0B]/5 border border-[#F59E0B] p-5">
                  <div className="flex items-center gap-3">
                    <UserCirclePlus size={20} weight="bold" className="text-[#F59E0B]" />
                    <div>
                      <p className="text-sm font-bold text-[#F59E0B]">{stats.inactive_users} utenti in attesa di attivazione</p>
                      <button onClick={() => navigate('/users')} className="text-xs text-[#F59E0B] font-bold hover:underline" data-testid="go-inactive-users">
                        Attiva →
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Team Leader specific */}
          {isTeamLeader && !isManager && !isAdmin && (
            <div className="bg-white border border-[#E4E4E7] p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-[#52525B]">Membri del Team</p>
                <span className="text-2xl font-bold font-heading">{stats?.team_members || 0}</span>
              </div>
              <button onClick={() => navigate('/calendar')} className="text-xs text-[#002FA7] font-bold mt-2 hover:underline" data-testid="go-team-calendar">
                Visualizza calendario team →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
